import update from 'immutability-helper';
import { SET_NEW_SHOPPINGCART } from './actionTypeConsts.js';
import {LEO_THEME} from "../dev";
import {getCountryCodeFromTheme} from "./customerReducer";

const initialTicketAddress = {
	countryCode: getCountryCodeFromTheme(),
	salutation: '',
	firstname: '',
	lastname: '',
	companyName: '',
	email: '',
	postalCode: '',
	city: '',
	street: '',
	houseNumber: '',
	phoneNumber: '',
};
export const initialState = {
	data: {},
	showTicketAddressData: false,
	showTicketAddressAdditionalDropDown: false,
	ticketAddressAdditionalDropDownMandatory: false,
	showTicketAddressFunctionDropDown: false,
	ticketAddressFunctionDropDownMandatory: false,
	errors: {},
};
// ------------------------------------
// Helper
// ------------------------------------

function generateMapForTicketAddressData(ticketItemUuids, ticketAddressData, shoppingCartDisplayTicketData) {
	const resultMap = {};
	if (!ticketItemUuids) return {};
	for (const ticketItemUuid of ticketItemUuids) {
		const ticketAddressDataObject = { address: {}, displayTicket: {} };
		ticketAddressDataObject.address = ticketAddressData[ticketItemUuid] ? ticketAddressData[ticketItemUuid].address : initialTicketAddress;
		ticketAddressDataObject.displayTicket = shoppingCartDisplayTicketData
			.filter(displayTicket => displayTicket.ticketItemUuid === ticketItemUuid)[0];
		resultMap[ticketItemUuid] = ticketAddressDataObject;
	}
	return resultMap;
}

function getDisplayTicketsPerTicketItemUuid(shoppingCart) {
	let displayTicketData = [];
	if (shoppingCart.events) {
		displayTicketData = shoppingCart.events
		.flatMap(scEvent => scEvent.displayTickets
			.flatMap(displayTicket =>
				displayTicket.ticketItemUuids.map(ticketItemUuid => {
					const aggregate = Object.assign(
						{},
						displayTicket,
						{ ticketItemUuid },
						{ event: scEvent.event },
						{ latestTicketUpdateOrCreateDate: scEvent.latestTicketUpdateOrCreateDate },
					);
					delete aggregate.ticketItemUuids;
					return aggregate;
				})
			)
		);
	}
	const sortedDisplayTicketData = Object.assign([], displayTicketData).sort((a, b) =>
		b.latestTicketUpdateOrCreateDate.localeCompare(a.latestTicketUpdateOrCreateDate) ||
		a.categoryType.localeCompare(b.categoryType) ||
		a.priceLevel.localeCompare(b.priceLevel) ||
		a.ticketItemUuid.localeCompare(b.ticketItemUuid));
	return sortedDisplayTicketData;
}

// ------------------------------------
// Constants
// ------------------------------------
export const SET_TICKET_ADDRESS_DATA = 'TICKET_ADDRESS/SET_TICKET_ADDRESS_DATA';
export const SET_TICKET_ADDRESS = 'TICKET_ADDRESS/SET_TICKET_ADDRESS';
export const SET_SHOW_TICKET_ADDRESS_DATA = 'TICKET_ADDRESS/SET_SHOW_TICKET_ADDRESS_DATA';
export const SET_SHOW_TICKET_ADDRESS_ADDITIONAL_DROPDOWN = 'TICKET_ADDRESS/SET_SHOW_TICKET_ADDRESS_ADDITIONAL_DROPDOWN';
export const SET_TICKET_ADDRESS_ADDITIONAL_DROPDOWN_MANDATORY = 'TICKET_ADDRESS/SET_TICKET_ADDRESS_ADDITIONAL_DROPDOWN_MANDATORY';
export const SET_SHOW_TICKET_ADDRESS_FUNCTION_DROPDOWN = 'TICKET_ADDRESS/SET_SHOW_TICKET_ADDRESS_FUNCTION_DROPDOWN';
export const SET_TICKET_ADDRESS_FUNCTION_DROPDOWN_MANDATORY = 'TICKET_ADDRESS/SET_TICKET_ADDRESS_FUNCTION_DROPDOWN_MANDATORY';
export const CLEAR_ERRORS = 'TICKET_ADDRESS/CLEAR_ERRORS';
export const SET_ERRORS = 'TICKET_ADDRESS/SET_ERRORS';

export function setShowTicketAddressFunctionDropDown(showTicketAddressFunctionDropDown) {
	return {
		type: SET_SHOW_TICKET_ADDRESS_FUNCTION_DROPDOWN,
		showTicketAddressFunctionDropDown,
	};
}

export function setTicketAddressFunctionDropDownMandatory(ticketAddressFunctionDropDownMandatory) {
	return {
		type: SET_TICKET_ADDRESS_FUNCTION_DROPDOWN_MANDATORY,
		ticketAddressFunctionDropDownMandatory,
	};
}

export function setTicketAddressData(ticketAddressData) {
	return {
		type: SET_TICKET_ADDRESS_DATA,
		ticketAddressData,
	};
}

export function setShowTicketAddressData(showTicketAddressData) {
	return {
		type: SET_SHOW_TICKET_ADDRESS_DATA,
		showTicketAddressData,
	};
}

export function setShowTicketAddressAdditionalDropDown(showTicketAddressAdditionalDropDown) {
	return {
		type: SET_SHOW_TICKET_ADDRESS_ADDITIONAL_DROPDOWN,
		showTicketAddressAdditionalDropDown,
	};
}

export function setTicketAddressAdditionalDropDownMandatory(ticketAddressAdditionalDropDownMandatory) {
	return {
		type: SET_TICKET_ADDRESS_ADDITIONAL_DROPDOWN_MANDATORY,
		ticketAddressAdditionalDropDownMandatory,
	};
}

export function setTicketAddress(address, ticketItemUuid) {
	return {
		type: SET_TICKET_ADDRESS,
		address,
		ticketItemUuid,
	};
}

export function clearTicketAddressErrors() {
	return {
		type: CLEAR_ERRORS,
	};
}

export function setTicketAddressErrors(errors) {
	return {
		type: SET_ERRORS,
		errors,
	};
}

// -------------------
// Action Creators
// -------------------
export function createOrUpdateTicketAddressData() {
	const themeModule = LEO_THEME.modules['ticket-address'] || {};
	const showTicketAddressData = themeModule['ticket-addresses-enabled'] === 'true';
	// "!!" to check if there's a value
	const showTicketAddressAdditionalDropDown = !!themeModule['ticket-address-additional-dropdown_values'];
	const ticketAddressAdditionalDropDownMandatory = themeModule['ticket-address-additional-dropdown_mandatory'] === 'true';

	// Function field
	const showTicketAddressFunctionDropDown = !!themeModule['ticket-address-function-dropdown_values'];
	const ticketAddressFunctionDropDownMandatory = themeModule['ticket-address-function-dropdown_mandatory'] === 'true';

	return (dispatch, getState) => {
		const shoppingCart = getState().shoppingCart.elements[getState().merchant.merchantId];
		let shoppingCartDisplayTicketData = getDisplayTicketsPerTicketItemUuid(shoppingCart);
		const tktsToPersData = shoppingCartDisplayTicketData.filter(dtd => dtd.personalize);
		const personalizationMode = !showTicketAddressData && tktsToPersData && tktsToPersData.length > 0;
		if (personalizationMode) {
			shoppingCartDisplayTicketData = tktsToPersData;
		}
		const ticketItemUuids = shoppingCartDisplayTicketData.flatMap(displayTicket => displayTicket.ticketItemUuid);
		const ticketAddressData = getState().ticketAddress.data;
		if (!showTicketAddressData && !personalizationMode) {
			// Reset TicketAddresses if there are any but not needed.
			if (Object.keys(getState().ticketAddress.data).length > 0) {
				dispatch(setTicketAddressData(initialState.data));
			}
			return Promise.resolve();
		}
		dispatch(setShowTicketAddressData(showTicketAddressData));
		dispatch(setShowTicketAddressAdditionalDropDown(showTicketAddressAdditionalDropDown));
		dispatch(setTicketAddressAdditionalDropDownMandatory(ticketAddressAdditionalDropDownMandatory));
		dispatch(setShowTicketAddressFunctionDropDown(showTicketAddressFunctionDropDown));
		dispatch(setTicketAddressFunctionDropDownMandatory(ticketAddressFunctionDropDownMandatory));
		return dispatch(setTicketAddressData(
			generateMapForTicketAddressData(
				ticketItemUuids,
				ticketAddressData,
				shoppingCartDisplayTicketData,
			),
		));
	};
}

export function updateTicketAddress(fieldName, value, ticketItemUuid) {
	return (dispatch, getState) => {
		const updatedAddress =
			Object.assign({}, getState().ticketAddress.data[ticketItemUuid].address, { [fieldName]: value });
		return dispatch(setTicketAddress(updatedAddress, ticketItemUuid));
	};
}


// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
	[SET_TICKET_ADDRESS_DATA]: (state, action) => update(state,	{
		data: { $set: action.ticketAddressData },
	}),
	[SET_SHOW_TICKET_ADDRESS_DATA]: (state, action) => update(state,	{
		showTicketAddressData: { $set: action.showTicketAddressData },
	}),
	[SET_SHOW_TICKET_ADDRESS_ADDITIONAL_DROPDOWN]: (state, action) => update(state,	{
		showTicketAddressAdditionalDropDown: { $set: action.showTicketAddressAdditionalDropDown },
	}),
	[SET_TICKET_ADDRESS_ADDITIONAL_DROPDOWN_MANDATORY]: (state, action) => update(state,	{
		ticketAddressAdditionalDropDownMandatory: { $set: action.ticketAddressAdditionalDropDownMandatory },
	}),
	[SET_SHOW_TICKET_ADDRESS_FUNCTION_DROPDOWN]: (state, action) => update(state,	{
		showTicketAddressFunctionDropDown: { $set: action.showTicketAddressFunctionDropDown },
	}),
	[SET_TICKET_ADDRESS_FUNCTION_DROPDOWN_MANDATORY]: (state, action) => update(state,	{
		ticketAddressFunctionDropDownMandatory: { $set: action.ticketAddressFunctionDropDownMandatory },
	}),
	[SET_TICKET_ADDRESS]: (state, action) => update(state,	{
		data: {
			[action.ticketItemUuid]: {
				address: { $set: action.address },
			},
		},
	}),
	[CLEAR_ERRORS]: (state) => update(state, {
		errors: { $set: initialState.errors },
	}),
	[SET_ERRORS]: (state, action) => {
		window.scrollTo(0, 0);
		return update(state, {
			errors: { $set: action.errors },
		});
	},
	[SET_NEW_SHOPPINGCART]: (state) => update(state,	{
		data: { $set: initialState.data },
	}),
};

// ------------------------------------
// Reducer
// ------------------------------------
export default function reducerFunction(state = initialState, action) {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
}
